import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { ISignatureRequest } from '@clickersphere/core/entities/ISignatureRequest';
import { ResponseError } from '@clickersphere/core/entities/ResponseError';
import { z } from 'zod';
import { useCreateSignatureRequest } from '../../../../app/hooks/contract-signature/useCreateSignatureRequest';
import { useError } from '../../../../app/hooks/useError';
import useToast from '../../../../app/hooks/useToast';
import { useStore } from '../../../../app/store';

export function useSendForSignatureModalController() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { handleError } = useError();

  const { sendForSignature, handleCloseModal } = useStore(
    useShallow((state) => ({
      sendForSignature: state.modal.sendForSignature,
      handleCloseModal: state.modal.handleCloseModal,
    })),
  );

  const { createSignatureRequestAsync, createSignatureRequestIsPending } =
    useCreateSignatureRequest();

  const [createdRequest, setCreatedRequest] =
    useState<ISignatureRequest | null>(null);
  const [copied, setCopied] = useState(false);

  const schema = useMemo(
    () =>
      z.object({
        signerName: z
          .string()
          .trim()
          .nonempty(t('contract.signature.modal.validation.signerNameRequired'))
          .max(
            256,
            t('contract.signature.modal.validation.signerNameMaxLength'),
          ),
        signerEmail: z
          .string()
          .trim()
          .max(256, t('contract.signature.modal.validation.signerEmailInvalid'))
          .email(t('contract.signature.modal.validation.signerEmailInvalid'))
          .optional()
          .or(z.literal('')),
        signerDocument: z
          .string()
          .trim()
          .max(
            18,
            t('contract.signature.modal.validation.signerDocumentInvalid'),
          )
          .optional()
          .or(z.literal('')),
      }),
    [t],
  );

  type FormData = z.infer<typeof schema>;

  const {
    register,
    handleSubmit: hookFormHandleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      signerName: sendForSignature?.signerName ?? '',
      signerEmail: sendForSignature?.signerEmail ?? '',
      signerDocument: '',
    },
  });

  const signerDocument = watch('signerDocument') ?? '';

  function handleSignerDocumentChange(value: string) {
    setValue('signerDocument', value, { shouldValidate: false });
  }

  const handleSubmit = hookFormHandleSubmit(async (data) => {
    if (!sendForSignature) return;

    try {
      const request = await createSignatureRequestAsync({
        leadId: sendForSignature.leadId,
        contractId: sendForSignature.contractId,
        data: {
          signerName: data.signerName.trim(),
          signerEmail: data.signerEmail?.trim() || undefined,
          signerDocument: data.signerDocument?.trim() || undefined,
        },
      });

      setCreatedRequest(request);
      toast(t('contract.signature.modal.toast.created'), 'success');
    } catch (error) {
      if (error instanceof ResponseError) {
        handleError(error);
        return;
      }
      toast(t('contract.signature.modal.toast.createError'), 'error');
    }
  });

  async function handleCopyLink() {
    if (!createdRequest) return;

    try {
      await navigator.clipboard.writeText(createdRequest.publicUrl);
      setCopied(true);
      toast(t('contract.signature.modal.toast.linkCopied'), 'success');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast(t('contract.signature.modal.toast.linkCopyError'), 'error');
    }
  }

  return {
    t,
    register,
    errors,
    signerDocument,
    createdRequest,
    copied,
    isSubmitting: createSignatureRequestIsPending,
    handleSignerDocumentChange,
    handleSubmit,
    handleCopyLink,
    handleCloseModal,
  };
}
